import { Component, OnInit, inject, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { EmployeeCostImportProxyService } from '../../../proxies/employee-cost-import.proxy.service';
import { EnumService } from '../../../services/enum.service';
import { getLocalizedValue, GlobalMessageUtil } from '@card-software/nexus-utilities';
import { EmployeeMonthlyCost } from '../../../models/monthly-uploads/employee-monthly-cost';

@Component({
  selector: 'app-employee-costs-tab',
  templateUrl: './employee-costs-tab.component.html',
  styleUrl: './employee-costs-tab.component.scss',
})
export class EmployeeCostsTabComponent implements OnInit {
  private readonly employeeCostImportProxyService = inject(EmployeeCostImportProxyService);
  private readonly translateService = inject(TranslateService);
  private readonly enumService = inject(EnumService);

  // Reactive Forms Property Pattern
  month = new FormControl<number | null>(null, [Validators.required]);
  year = new FormControl<number | null>(null, [Validators.required]);
  formGroup = new FormGroup({
    month: this.month,
    year: this.year,
  });
  currency = new FormControl<number | null>(null, [Validators.required]);

  selectedFile: File | null = null;
  isLoading = signal(false);
  employeeMonthlyCosts = signal<EmployeeMonthlyCost[]>([]);
  showUploadUi = signal(false);

  monthOptions: { label: string; value: number }[] = [
    { label: 'January', value: 1 },
    { label: 'February', value: 2 },
    { label: 'March', value: 3 },
    { label: 'April', value: 4 },
    { label: 'May', value: 5 },
    { label: 'June', value: 6 },
    { label: 'July', value: 7 },
    { label: 'August', value: 8 },
    { label: 'September', value: 9 },
    { label: 'October', value: 10 },
    { label: 'November', value: 11 },
    { label: 'December', value: 12 },
  ];

  yearOptions: { label: string; value: number }[] = (() => {
    const currentYear = new Date().getFullYear();
    return [currentYear, currentYear - 1, currentYear - 2, currentYear - 3, currentYear - 4].map((v) => ({
      label: String(v),
      value: v,
    }));
  })();

  currencyOptions: { label: string; value: number }[] = this.getCurrencyOptions();

  ngOnInit(): void {
    this.loadEmployeeMonthlyCosts();
  }

  canUpload(): boolean {
    return this.month.value !== null && this.year.value !== null && this.currency.value !== null;
  }

  canImport(): boolean {
    return this.canUpload() && this.selectedFile !== null;
  }

  onFileChange(e: Event): void {
    const input = e.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }
    const file = input.files[0];
    if (!this.isXlsx(file)) {
      GlobalMessageUtil.warn(
        this.translateService.instant('Employee_Costs_Invalid_File'),
        this.translateService.instant('Employee_Costs_Invalid_File'),
      );
      input.value = '';
      return;
    }
    this.selectedFile = file;
    input.value = '';
  }

  clearFile(): void {
    this.selectedFile = null;
  }

  import(): void {
    if (!this.canImport() || this.isLoading()) {
      return;
    }

    this.isLoading.set(true);
    this.employeeCostImportProxyService
      .importExcel(this.month.value!, this.year.value!, this.currency.value!, this.selectedFile!)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (response) => {
          if (response.success) {
            const totalInserted = response.data?.totalInserted ?? 0;
            this.loadEmployeeMonthlyCosts();
            this.clearFile();
            this.showUploadUi.set(false);
            GlobalMessageUtil.success(this.translateService.instant('Employee_Costs_Import_Success'), `Total inserted: ${totalInserted}`);
          } else {
            GlobalMessageUtil.error(
              this.translateService.instant('Employee_Costs_Import_Error'),
              getLocalizedValue(this.translateService, response.errorMessage) ||
                this.translateService.instant('Employee_Costs_Import_Error'),
            );
          }
        },
      });
  }

  openUploadUi(): void {
    this.showUploadUi.set(true);
  }

  cancelUploadUi(): void {
    this.clearFile();
    this.showUploadUi.set(false);
  }

  loadEmployeeMonthlyCosts(): void {
    this.isLoading.set(true);
    this.employeeCostImportProxyService
      .getAll(this.month.value ?? undefined, this.year.value ?? undefined, this.currency.value ?? undefined)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.employeeMonthlyCosts.set(response.data);
          } else {
            this.employeeMonthlyCosts.set([]);
          }
        },
        error: () => {
          this.employeeMonthlyCosts.set([]);
        },
      });
  }

  private isXlsx(f: File): boolean {
    return /\.xlsx$/i.test(f.name);
  }

  private getCurrencyOptions(): { label: string; value: number }[] {
    const enumValues = this.enumService.getEnum('Currency').values;
    const mapped = Object.entries(enumValues).map(([key, label]) => ({
      label,
      value: Number(key),
    }));

    if (mapped.length > 0) {
      return mapped;
    }

    return [
      { label: 'USD', value: 0 },
      { label: 'HNL', value: 1 },
      { label: 'EUR', value: 2 },
    ];
  }

  getCurrencyLabel(currencyValue: number | null | undefined): string {
    if (currencyValue === null || currencyValue === undefined) {
      return '------';
    }
    return this.currencyOptions.find((x) => x.value === currencyValue)?.label ?? String(currencyValue);
  }

  getMonthLabel(monthValue: number | null | undefined): string {
    if (monthValue === null || monthValue === undefined) {
      return '------';
    }
    return this.monthOptions.find((x) => x.value === monthValue)?.label ?? String(monthValue);
  }

  displayValue(value: string | number | null | undefined): string {
    if (value === null || value === undefined || value === '') {
      return '------';
    }
    return String(value);
  }
}
